import React, { useState } from 'react';
import './FeedbackWidget.css';
import { landlordApi } from '../../services/api';

export default function FeedbackWidget() {
    const [isOpen, setIsOpen] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [category, setCategory] = useState('feature');
    const [submitted, setSubmitted] = useState(false);
    const [sending, setSending] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSending(true);
        setErrorMsg(null);

        try {
            const userEmail = localStorage.getItem('userEmail') || 'anonymous';

            // No leading slash — with a leading slash axios resolves against
            // the origin and drops the /prod2/ API-Gateway stage from the
            // configured baseURL, so the request 404s silently.
            const response = await landlordApi.post('api/support/feedback', {
                category,
                feedback,
                userEmail
            });

            if (response.data?.success === false) {
                throw new Error(response.data.message || 'Server rejected feedback');
            }

            console.log('[Feedback] Sent successfully:', response.data);
            setSubmitted(true);
            setTimeout(() => {
                setIsOpen(false);
                setSubmitted(false);
                setFeedback('');
            }, 2000);
        } catch (error: any) {
            console.error('[Feedback] Failed:', error);
            setErrorMsg(error?.response?.data?.message || error?.message || 'Could not send feedback. Please try again or email hello@compliance-engine.io.');
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            {/* Floating Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${isOpen ? 'scale-0' : 'scale-100'
                    }`}
                aria-label="Share Feedback"
            >
                <div className="relative group">
                    <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full blur opacity-75 group-hover:opacity-100 transition duration-200"></div>
                    <div className="relative bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110">
                        <i className="fas fa-lightbulb text-xl"></i>
                    </div>
                </div>
            </button>

            {/* Feedback Panel */}
            {isOpen && (
                <div className="fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] animate-slide-up">
                    <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                        {/* Header */}
                        <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-4 text-white">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                    <i className="fas fa-lightbulb text-xl"></i>
                                    <h3 className="font-bold text-lg">Shape Our Future!</h3>
                                </div>
                                <button
                                    onClick={() => setIsOpen(false)}
                                    className="text-white hover:text-gray-200 transition-colors"
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            <p className="text-sm text-purple-100 mt-1">
                                Your ideas drive our roadmap ✨
                            </p>
                        </div>

                        {/* Content */}
                        <div className="p-4">
                            {submitted ? (
                                <div className="text-center py-8">
                                    <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                                        <i className="fas fa-check text-3xl text-green-600"></i>
                                    </div>
                                    <h4 className="text-lg font-bold text-gray-900 mb-2">Thanks! 🎉</h4>
                                    <p className="text-sm text-gray-600">
                                        Your feedback helps us build better features!
                                    </p>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    {/* Category Selection */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            What's on your mind?
                                        </label>
                                        <div className="grid grid-cols-3 gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setCategory('feature')}
                                                className={`p-3 rounded-lg border-2 transition-all ${category === 'feature'
                                                    ? 'border-purple-500 bg-purple-50 text-purple-700'
                                                    : 'border-gray-200 hover:border-purple-300'
                                                    }`}
                                            >
                                                <i className="fas fa-star block mb-1"></i>
                                                <span className="text-xs font-medium">Feature</span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setCategory('improvement')}
                                                className={`p-3 rounded-lg border-2 transition-all ${category === 'improvement'
                                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                                    : 'border-gray-200 hover:border-blue-300'
                                                    }`}
                                            >
                                                <i className="fas fa-wrench block mb-1"></i>
                                                <span className="text-xs font-medium">Improve</span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setCategory('bug')}
                                                className={`p-3 rounded-lg border-2 transition-all ${category === 'bug'
                                                    ? 'border-red-500 bg-red-50 text-red-700'
                                                    : 'border-gray-200 hover:border-red-300'
                                                    }`}
                                            >
                                                <i className="fas fa-bug block mb-1"></i>
                                                <span className="text-xs font-medium">Bug</span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Feedback Input */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Tell us more
                                        </label>
                                        <textarea
                                            value={feedback}
                                            onChange={(e) => setFeedback(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                                            rows={4}
                                            placeholder="What feature would make your life easier? 🚀"
                                            required
                                        />
                                    </div>

                                    {errorMsg && (
                                        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">
                                            <i className="fas fa-exclamation-triangle mr-1"></i>{errorMsg}
                                        </div>
                                    )}

                                    {/* Submit Button */}
                                    <button
                                        type="submit"
                                        disabled={sending}
                                        className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold py-3 rounded-lg hover:shadow-lg transition-all duration-200 hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                                    >
                                        <i className={`fas ${sending ? 'fa-spinner fa-spin' : 'fa-paper-plane'} mr-2`}></i>
                                        {sending ? 'Sending…' : 'Send Feedback'}
                                    </button>

                                    {/* Fun Footer */}
                                    <p className="text-xs text-center text-gray-500">
                                        💡 Popular requests get built first!
                                    </p>
                                </form>
                            )}
                        </div>

                        {/* Quick Links */}
                        {!submitted && (
                            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                                <div className="flex items-center justify-between text-xs">
                                    <a
                                        href="/roadmap"
                                        className="text-purple-600 hover:text-purple-700 font-medium flex items-center"
                                    >
                                        <i className="fas fa-map-signs mr-1"></i>
                                        View Roadmap
                                    </a>
                                    <a
                                        href="/support"
                                        className="text-gray-600 hover:text-gray-700 font-medium flex items-center"
                                    >
                                        <i className="fas fa-life-ring mr-1"></i>
                                        Get Help
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
